import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  LinearProgress,
  CircularProgress,
  Divider,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  HelpOutline,
  Receipt as ReceiptIcon,
  Calculate as CalculateIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import api from "../../services/api";

// デフォルト設定値
const DEFAULT_SETTINGS = {
  // Phase 1: 税金設定
  taxCalculationType: "exclusive",
  standardTaxRate: 10,
  reducedTaxRate: 8,
  useReducedTaxRate: false,
  roundingMethod: "floor",

  // Phase 2: レシート設定
  receiptHeader: "",
  receiptFooter: "",
  receiptLogoUrl: null,
  printLogoOnReceipt: false,
  receiptTemplate: "standard",

  // Phase 3: 会計期間設定
  fiscalYearStartMonth: 4,
  monthlyClosingDay: 0,
  businessDayChangeHour: 5,
};

const TaxReceiptSettings = () => {
  const { storeId } = useParams();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    tax: true,
    receipt: false,
    accounting: false,
  });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/stores/${storeId}/store-settings`);
      setSettings((prev) => ({ ...prev, ...response.data }));
    } catch (err) {
      console.error("設定の取得に失敗しました:", err);
      setError("設定の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox"
        ? e.target.checked
        : e.target.type === "number"
          ? parseFloat(e.target.value)
          : e.target.value;
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      await api.put(`/api/stores/${storeId}/store-settings`, settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("設定の更新に失敗しました:", err);
      setError(err.response?.data?.message || "設定の更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // 税額計算プレビュー
  const calculateTaxPreview = (amount) => {
    const taxRate =
      settings.useReducedTaxRate
        ? settings.reducedTaxRate
        : settings.standardTaxRate;

    let taxAmount;
    let priceWithTax;
    let priceWithoutTax;

    if (settings.taxCalculationType === "inclusive") {
      priceWithTax = amount;
      priceWithoutTax = amount / (1 + taxRate / 100);
      taxAmount = priceWithTax - priceWithoutTax;
    } else {
      priceWithoutTax = amount;
      taxAmount = amount * (taxRate / 100);
      priceWithTax = amount + taxAmount;
    }

    switch (settings.roundingMethod) {
      case "round":
        taxAmount = Math.round(taxAmount);
        priceWithTax = Math.round(priceWithTax);
        break;
      case "ceil":
        taxAmount = Math.ceil(taxAmount);
        priceWithTax = Math.ceil(priceWithTax);
        break;
      case "floor":
      default:
        taxAmount = Math.floor(taxAmount);
        priceWithTax = Math.floor(priceWithTax);
        break;
    }

    return { taxAmount, priceWithTax, priceWithoutTax: Math.round(priceWithoutTax) };
  };

  const taxPreview = calculateTaxPreview(1000);

  return (
    <Box aria-busy={loading || saving}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Typography variant="h4" gutterBottom>
        税金・レシート設定
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        消費税の計算方法、レシートの表示内容、会計期間の設定を行います。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          設定を更新しました
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Phase 1: 税金設定 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSection("tax")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <CalculateIcon color="primary" />
                    <Typography variant="h6">税金設定</Typography>
                  </Box>
                  <IconButton size="small">
                    {expandedSections.tax ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSections.tax}>
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      {/* 内税/外税 */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>税計算方式</InputLabel>
                          <Select
                            value={settings.taxCalculationType}
                            onChange={handleChange("taxCalculationType")}
                            label="税計算方式"
                            disabled={saving}
                          >
                            <MenuItem value="exclusive">
                              外税（税別価格を表示）
                            </MenuItem>
                            <MenuItem value="inclusive">
                              内税（税込価格を表示）
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* 端数処理 */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>端数処理</InputLabel>
                          <Select
                            value={settings.roundingMethod}
                            onChange={handleChange("roundingMethod")}
                            label="端数処理"
                            disabled={saving}
                          >
                            <MenuItem value="floor">切り捨て</MenuItem>
                            <MenuItem value="round">四捨五入</MenuItem>
                            <MenuItem value="ceil">切り上げ</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* 標準税率 */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="標準税率 (%)"
                          type="number"
                          value={settings.standardTaxRate}
                          onChange={handleChange("standardTaxRate")}
                          disabled={saving}
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                          InputProps={{
                            endAdornment: (
                              <Tooltip title="通常商品に適用される税率">
                                <IconButton size="small">
                                  <HelpOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ),
                          }}
                        />
                      </Grid>

                      {/* 軽減税率 */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="軽減税率 (%)"
                          type="number"
                          value={settings.reducedTaxRate}
                          onChange={handleChange("reducedTaxRate")}
                          disabled={saving || !settings.useReducedTaxRate}
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                          InputProps={{
                            endAdornment: (
                              <Tooltip title="テイクアウト等に適用される軽減税率">
                                <IconButton size="small">
                                  <HelpOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ),
                          }}
                        />
                      </Grid>

                      {/* 軽減税率使用フラグ */}
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.useReducedTaxRate}
                              onChange={handleChange("useReducedTaxRate")}
                              disabled={saving}
                            />
                          }
                          label="軽減税率を使用する（テイクアウト対応）"
                        />
                      </Grid>

                      {/* 税計算プレビュー */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          計算例（1,000円の商品）
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          <Chip
                            label={`税抜: ¥${taxPreview.priceWithoutTax.toLocaleString()}`}
                            variant="outlined"
                          />
                          <Chip
                            label={`税額: ¥${taxPreview.taxAmount.toLocaleString()}`}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`税込: ¥${taxPreview.priceWithTax.toLocaleString()}`}
                            color="success"
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

          {/* Phase 2: レシート設定 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSection("receipt")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <ReceiptIcon color="primary" />
                    <Typography variant="h6">レシート設定</Typography>
                  </Box>
                  <IconButton size="small">
                    {expandedSections.receipt ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSections.receipt}>
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      {/* ヘッダー */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="レシートヘッダー（3行まで）"
                          multiline
                          rows={3}
                          value={settings.receiptHeader}
                          onChange={handleChange("receiptHeader")}
                          disabled={saving}
                          placeholder="ご来店ありがとうございます&#10;営業時間: 11:00-22:00&#10;TEL: 03-xxxx-xxxx"
                          helperText="レシート上部に印刷されるメッセージ（改行で複数行）"
                        />
                      </Grid>

                      {/* フッター */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="レシートフッター（3行まで）"
                          multiline
                          rows={3}
                          value={settings.receiptFooter}
                          onChange={handleChange("receiptFooter")}
                          disabled={saving}
                          placeholder="またのご来店をお待ちしております&#10;ポイントカード発行中！"
                          helperText="レシート下部に印刷されるメッセージ（改行で複数行）"
                        />
                      </Grid>

                      {/* ロゴURL */}
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          label="店舗ロゴURL"
                          value={settings.receiptLogoUrl || ""}
                          onChange={handleChange("receiptLogoUrl")}
                          disabled={saving}
                          placeholder="https://example.com/logo.png"
                          helperText="レシートに印刷する店舗ロゴ画像のURL"
                        />
                      </Grid>

                      {/* ロゴ印刷フラグ */}
                      <Grid item xs={12} sm={4}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.printLogoOnReceipt}
                              onChange={handleChange("printLogoOnReceipt")}
                              disabled={saving || !settings.receiptLogoUrl}
                            />
                          }
                          label="ロゴを印刷"
                        />
                      </Grid>

                      {/* テンプレート */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>レシートテンプレート</InputLabel>
                          <Select
                            value={settings.receiptTemplate}
                            onChange={handleChange("receiptTemplate")}
                            label="レシートテンプレート"
                            disabled={saving}
                          >
                            <MenuItem value="standard">標準</MenuItem>
                            <MenuItem value="detailed">詳細（税内訳表示）</MenuItem>
                            <MenuItem value="simple">シンプル</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

          {/* Phase 3: 会計期間設定 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSection("accounting")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <CalendarIcon color="primary" />
                    <Typography variant="h6">会計期間設定</Typography>
                  </Box>
                  <IconButton size="small">
                    {expandedSections.accounting ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSections.accounting}>
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      {/* 年度開始月 */}
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>年度開始月</InputLabel>
                          <Select
                            value={settings.fiscalYearStartMonth}
                            onChange={handleChange("fiscalYearStartMonth")}
                            label="年度開始月"
                            disabled={saving}
                          >
                            {[...Array(12)].map((_, i) => (
                              <MenuItem key={i + 1} value={i + 1}>
                                {i + 1}月
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* 月締め日 */}
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>月締め日</InputLabel>
                          <Select
                            value={settings.monthlyClosingDay}
                            onChange={handleChange("monthlyClosingDay")}
                            label="月締め日"
                            disabled={saving}
                          >
                            <MenuItem value={0}>月末</MenuItem>
                            {[...Array(31)].map((_, i) => (
                              <MenuItem key={i + 1} value={i + 1}>
                                {i + 1}日
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* 営業日変更時刻 */}
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>営業日変更時刻</InputLabel>
                          <Select
                            value={settings.businessDayChangeHour}
                            onChange={handleChange("businessDayChangeHour")}
                            label="営業日変更時刻"
                            disabled={saving}
                          >
                            {[...Array(24)].map((_, i) => (
                              <MenuItem key={i} value={i}>
                                {i}:00
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* 説明 */}
                      <Grid item xs={12}>
                        <Alert severity="info" icon={<HelpOutline />}>
                          <Typography variant="body2">
                            <strong>営業日変更時刻</strong>: 深夜営業の店舗では、
                            例えば午前5時に設定すると、0:00〜4:59の売上は前日の売上として集計されます。
                          </Typography>
                        </Alert>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

          {/* 保存ボタン */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={saving}
            >
              {saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "設定を保存"
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default TaxReceiptSettings;
